import {
  EditType,
  OrganizationType,
  PositionType,
  type ProfileEditReviewItemRes,
  RecruitmentStatus,
  ReviewItemStatus,
  type TopInterestProfileEdit,
  v1PeopleProfileEditsCreate,
} from '@on3/api';
import { OrganizationAutoComplete } from '@on3/ui-lib/components/AutoComplete/OrganizationAutoComplete';
import { PersonAutoComplete } from '@on3/ui-lib/components/AutoComplete/PersonAutoComplete';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Form } from '@on3/ui-lib/components/Form';
import { Select } from '@on3/ui-lib/components/Select/Select';
import { externalApi, useToast } from '@on3/ui-lib/index';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import {
  protectedRecruitmentStatuses,
  type TopInterest,
  topInterestSchema,
  useRecruitmentStatusOptions,
} from '@public/components/Elite/Profile/Edits/TopInterests';
import { usePlayerProfile } from '@public/contexts/PlayerProfile/PlayerProvider';
import { createTitle } from '@public/utils/meta';
import { useState } from 'react';

import styles from './CreateTeamModal.module.scss';

interface ICreateTeamModalProps {
  onComplete: (topInterest: TopInterestProfileEdit) => void;
  updateEdits: (reviewItem: ProfileEditReviewItemRes) => void;
  order: number;
  protectedTeam?: TopInterest;
  existingOrgs?: number[];
}

export const CreateTeamModal = ({
  onComplete = () => {},
  updateEdits = () => {},
  order,
  protectedTeam,
  existingOrgs = [],
}: ICreateTeamModalProps) => {
  const {
    state: { player },
  } = usePlayerProfile();
  const { notify } = useToast();
  const recruitmentStatusOptions = useRecruitmentStatusOptions(protectedTeam);

  const [values, setValues] = useState<TopInterest>({
    $type: 'topInterest',
    editType: EditType.Add,
    order,
    recruitmentStatus: RecruitmentStatus.Interested,
    contacted: false,
    offer: false,
    organizationKey: 0,
    organizationName: '',
    organizationAssetUrl: '',
    primaryPersonKey: null,
    primaryPersonName: '',
    recruitmentKey: player.recruitmentKey,
  });

  const handleUpdate = (field: keyof TopInterest, value: string | number) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async ({ $type, editType, ...edit }: TopInterest) => {
    if (existingOrgs?.includes(edit.organizationKey)) {
      notify('You cannot select the same team twice.', { type: 'error' });

      return;
    }

    try {
      const newInterest = {
        $type,
        editType,
        ...edit,
      };

      const { reviewItem } = await v1PeopleProfileEditsCreate(
        externalApi,
        player.key,
        {
          edit: newInterest,
        },
      );

      notify('Team added successfully.', { type: 'success' });

      if (reviewItem.status === ReviewItemStatus.Pending) {
        updateEdits(reviewItem);
      }

      // TODO: Update this to use the new API schema
      onComplete(newInterest as TopInterestProfileEdit);
    } catch (error) {
      notify('Failed to add team.', { type: 'error' });
    }
  };

  const isValid = !!values.organizationKey;

  return (
    <Form
      className={styles.block}
      onSubmit={handleSubmit}
      schema={topInterestSchema}
      values={values}
    >
      {({ processing, errors }) => (
        <>
          <h2>Add Team</h2>
          <OrganizationAutoComplete
            className={styles.autoComplete}
            error={errors.organizationKey}
            id="team"
            label="Team"
            limit={1}
            name="organizationKey"
            onChange={(val) => {
              handleUpdate('organizationKey', val[0]?.key);
              handleUpdate('organizationName', val[0]?.selectedLabel as string);
              handleUpdate('organizationAssetUrl', val[0]?.assetUrl);
            }}
            optionTemplate={(o) => ({
              key: o.key,
              label: createTitle(o.knownAs ?? o.name ?? '', o.mascot),
              selectedLabel: createTitle(o.knownAs ?? o.name ?? '', o.mascot),
              assetUrl: getAssetUrl(o.defaultAsset),
            })}
            placeholder="Search for team..."
            prefix={
              values.organizationKey && (
                <figure className={styles.avatar}>
                  <Avatar
                    altText={values.organizationName}
                    height={20}
                    source={values.organizationAssetUrl}
                    teamAvatar
                    width={20}
                  />
                </figure>
              )
            }
            type={OrganizationType.College}
            value={[
              {
                key: values.organizationKey ?? 0,
                label: values.organizationName ?? '',
                assetUrl: values.organizationAssetUrl ?? '',
              },
            ].filter((t) => t?.key)}
          />
          <Select
            label="Recruitment Status"
            name="recruitmentStatus"
            onChange={(v) => handleUpdate('recruitmentStatus', v.target.value)}
            options={recruitmentStatusOptions.filter(
              (so) => !protectedRecruitmentStatuses.includes(so.value),
            )}
            value={values.recruitmentStatus}
          />
          <PersonAutoComplete
            disabled={!values.organizationKey}
            error={errors.primaryPersonKey}
            id="recruitedBy"
            label="Recruited By"
            limit={1}
            name="primaryPersonKey"
            onChange={(val) => {
              handleUpdate('primaryPersonKey', val[0]?.key);
              handleUpdate('primaryPersonName', val[0]?.label);
            }}
            optionTemplate={(c) => ({
              key: c.key,
              label: createTitle(
                c.firstName,
                c.lastName,
                c.currentOrganization?.name &&
                  `(${c.currentOrganization?.name})`,
              ),
              selectedLabel: createTitle(c.firstName, c.lastName),
            })}
            placeholder="Search for recruiter..."
            searchParams={{
              positionTypes: [PositionType.Coach],
              currentOrganizationKey: values.organizationKey,
            }}
            value={[
              {
                key: values.primaryPersonKey ?? 0,
                label: values.primaryPersonName ?? '',
              },
            ].filter((c) => c?.key)}
          />
          <button disabled={processing || !isValid} type="submit">
            Save
          </button>
        </>
      )}
    </Form>
  );
};
